import { useEffect, useMemo, useState } from 'react';
import {
  api, type CommerceOrgRow, type CorrectionReason, type CorrectionsKind, type GlOptions, type InvoiceQueueRow, type InvoiceWorkflow,
  type KeyingOrder, type KeyingOrderRow, type KeyingVendor, type LineCorrection, type PackageRow,
} from '../api';
import { label, money } from '../lib';
import { InvoiceHistoryLog } from './InvoiceHistory';

// The AP write workflow — keying, the queue, line corrections, status advances, PDF attach/view,
// and packaging.
const STATUSES = ['keyed', 'review', 'review_edi', 'hold_for_follow_up', 'released_to_client',
  'client_review', 'client_returned', 'has_pdf', 'pdf_skipped', 'approved', 'packaged'];
const today = () => new Date().toLocaleDateString('en-CA');
const errText = (e: unknown) => (e as Error)?.message ?? String(e);

type LineDraft = { itemNumber: string; itemName: string; qty: string; price: string };

// Keying a paper invoice: the camp and the vendor printed on it FIRST, then the PO, as production's
// keying form asks (Invoices/Controller/Adminhtml/Invoice/Save.php:222-230). Commerce refuses a PO that
// is not that camp's from that vendor (422), and the lookups only ever offer ones that are — so a
// mistyped PO number cannot file one camp's bill under another. Vendors are the ones on the camp's POs,
// including any it no longer has an active assignment with, plus the vendors it is assigned (an invoice
// with no PO is keyed only for one of those). The PO's lines prefill; edit them to match the paper, and
// drop any line it does not bill.
//
// An invoice that matches NO PO (go/no-go #16) is keyed as production keys one with the PO left blank
// (Save.php:203-265): its header from the paper — total, GL category, tax — and no lines. Anything on it
// to correct (freight not owed, a surcharge) is then added as a line with its correction type.
function KeyInvoiceForm({ onClose, onSaved }: { onClose: () => void; onSaved: (id: number) => void }) {
  const [campQ, setCampQ] = useState('');
  const [camps, setCamps] = useState<CommerceOrgRow[] | null>(null);
  const [camp, setCamp] = useState<CommerceOrgRow | null>(null);
  const [vendors, setVendors] = useState<KeyingVendor[] | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [poQ, setPoQ] = useState('');
  const [orders, setOrders] = useState<KeyingOrderRow[] | null>(null);
  const [order, setOrder] = useState<KeyingOrder | null>(null);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [noPo, setNoPo] = useState(false);
  const [head, setHead] = useState({ invoiceNo: '', invoiceDate: today(), glCategory: '', tax: '', invoiceTotal: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (camp || !campQ.trim()) return;
    let live = true;
    const t = setTimeout(() => {
      api.commerceOrgs(campQ.trim()).then((r) => live && setCamps(r)).catch((e) => live && setErr(errText(e)));
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [campQ, camp]);

  useEffect(() => {
    setVendors(null);
    if (!camp) return;
    let live = true;
    api.commerceKeyingVendors(camp.id, true).then((r) => live && setVendors(r)).catch((e) => { if (live) { setVendors([]); setErr(errText(e)); } });
    return () => { live = false; };
  }, [camp]);

  useEffect(() => {
    setOrders(null);
    if (!camp || !vendorId || order || noPo) return;
    let live = true;
    const t = setTimeout(() => {
      api.commerceKeyingOrders(camp.id, Number(vendorId), poQ.trim()).then((r) => live && setOrders(r)).catch((e) => live && setErr(errText(e)));
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [camp, vendorId, poQ, order, noPo]);

  const pickCamp = (c: CommerceOrgRow | null) => { setCamp(c); setCampQ(''); setCamps(null); setVendorId(''); setPoQ(''); setOrder(null); setLines([]); setNoPo(false); setErr(''); };
  const pickVendor = (id: string) => { setVendorId(id); setPoQ(''); setOrder(null); setLines([]); setNoPo(false); setErr(''); };
  async function pickOrder(id: number) {
    if (!camp || !vendorId) return;
    setErr('');
    try {
      const d = await api.commerceKeyingOrder(id, camp.id, Number(vendorId));
      setOrder(d);
      setLines(d.lines.map((l) => ({ itemNumber: l.itemNumber ?? '', itemName: l.itemName ?? '', qty: String(l.qty), price: String(l.price) })));
    } catch (e) { setErr(errText(e)); }
  }

  const total = useMemo(() => lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price) || 0), 0), [lines]);
  const setLine = (n: number, patch: Partial<LineDraft>) => setLines(lines.map((x, i) => (i === n ? { ...x, ...patch } : x)));

  async function saveWithoutPo() {
    if (!camp || !vendorId) return;
    const total = Number(head.invoiceTotal);
    if (head.invoiceTotal.trim() === '' || !Number.isFinite(total)) { setErr('Enter the total printed on the invoice.'); return; }
    if (!head.glCategory.trim()) { setErr('Pick the GL category the invoice is filed under — it is its SUM report row.'); return; }
    if (head.tax.trim() !== '' && !Number.isFinite(Number(head.tax))) { setErr('Tax must be a number.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.commerceKeyInvoice({
        orgId: camp.id, vendorId: Number(vendorId), invoiceNo: head.invoiceNo.trim(), invoiceDate: head.invoiceDate,
        glCategory: head.glCategory.trim(), invoiceTotal: total, tax: head.tax.trim() === '' ? undefined : Number(head.tax),
      });
      onSaved(r.id);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }

  async function save() {
    if (!camp || !vendorId || !order) return;
    if (lines.some((l) => l.qty.trim() === '' || !(Number(l.qty) > 0) || l.price.trim() === '' || !(Number(l.price) >= 0))) {
      setErr('Every line needs a quantity above 0 and a price of 0 or more — remove a line the invoice does not bill.');
      return;
    }
    if (head.tax.trim() !== '' && !Number.isFinite(Number(head.tax))) { setErr('Tax must be a number.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.commerceKeyInvoice({
        orderId: order.order.id, orgId: camp.id, vendorId: Number(vendorId),
        invoiceNo: head.invoiceNo.trim(), invoiceDate: head.invoiceDate,
        glCategory: head.glCategory.trim() || undefined, tax: head.tax.trim() === '' ? undefined : Number(head.tax),
        lines: lines.map((l) => ({ itemNumber: l.itemNumber.trim() || null, itemName: l.itemName.trim() || null, qty: Number(l.qty), price: Number(l.price) })),
      });
      onSaved(r.id);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }

  const vendor = vendors?.find((v) => String(v.id) === vendorId);
  const assigned = vendor?.assigned === true;
  return (
    <section className="card key-form">
      <div className="file-head">
        <h2 style={{ margin: 0 }}>Key an invoice</h2>
        <span style={{ flex: 1 }} />
        <button className="btn-sm ghost" onClick={onClose}>Cancel</button>
      </div>
      <div className="form-row">
        <label className="field"><span>1 · Camp on the invoice</span>
          <input className="search" placeholder="search camps…" value={camp ? camp.name : campQ} autoFocus
            onChange={(e) => { if (camp) pickCamp(null); setCampQ(e.target.value); }} />
          {!camp && campQ.trim() && camps && (
            <div className="typeahead">
              {camps.slice(0, 8).map((c) => <button key={c.id} className="ta-opt" onClick={() => pickCamp(c)}>{c.name}</button>)}
              {camps.length === 0 && <div className="loading" style={{ padding: 8 }}>No active camp matches.</div>}
            </div>
          )}
        </label>
        <label className="field"><span>2 · Vendor on the invoice</span>
          <select className="sel" value={vendorId} disabled={!camp || !vendors} onChange={(e) => pickVendor(e.target.value)}>
            <option value="">{!camp ? 'Pick the camp first' : vendors ? (vendors.length ? 'Choose the vendor…' : 'This camp has no POs') : 'Loading…'}</option>
            {(vendors ?? []).map((v) => (
              <option key={v.id} value={v.id}>{v.name} — {v.orders} PO{v.orders === 1 ? '' : 's'}{v.last_order_at ? `, last ${v.last_order_at.slice(0, 10)}` : ''}{v.assigned === false ? ' (no longer assigned)' : ''}</option>
            ))}
          </select>
        </label>
        {!order && !noPo && (
          <label className="field"><span>3 · PO on the invoice</span>
            <input className="search" placeholder="PO # or order #…" value={poQ} disabled={!vendorId} onChange={(e) => setPoQ(e.target.value)} />
          </label>
        )}
        {!order && vendorId && (
          <button className="btn-sm ghost" disabled={!noPo && !assigned}
            title={assigned ? 'The paper invoice names no PO, or none of this camp\'s POs' : 'An invoice with no PO is keyed only for a vendor the camp is assigned'}
            onClick={() => { setNoPo(!noPo); setErr(''); }}>{noPo ? '← Back to its POs' : 'No PO on this invoice'}</button>
        )}
      </div>
      {noPo && camp && vendorId && (
        <>
          <p className="muted" style={{ margin: '0.9rem 0 0.6rem' }}>
            No PO · {camp.name} · {vendor?.name} — keyed as its header only; add each line to correct (freight, a surcharge) afterwards, with its correction type.
          </p>
          <div className="form-row">
            <label className="field"><span>Invoice #</span><input className="search short" value={head.invoiceNo} onChange={(e) => setHead({ ...head, invoiceNo: e.target.value })} autoFocus /></label>
            <label className="field"><span>Invoice date</span><input className="search short" type="date" value={head.invoiceDate} onChange={(e) => setHead({ ...head, invoiceDate: e.target.value })} style={{ width: 150 }} /></label>
            <label className="field"><span>Invoice total</span><input className="search short" inputMode="decimal" value={head.invoiceTotal} onChange={(e) => setHead({ ...head, invoiceTotal: e.target.value })} placeholder="0.00" /></label>
            <label className="field"><span>GL category</span><input className="search short" value={head.glCategory} onChange={(e) => setHead({ ...head, glCategory: e.target.value })} placeholder="e.g. Food" /></label>
            <label className="field"><span>Tax</span><input className="search short" inputMode="decimal" value={head.tax} onChange={(e) => setHead({ ...head, tax: e.target.value })} placeholder="0.00" /></label>
          </div>
          <div className="edi-actions">
            <span className="muted">No PO is advanced — there is none. The GL category is the SUM report row the invoice is filed under.</span>
            <button className="btn-sm" disabled={busy || !head.invoiceNo.trim() || !head.invoiceDate || !head.invoiceTotal.trim() || !head.glCategory.trim()} onClick={saveWithoutPo}>{busy ? 'Saving…' : 'Save invoice'}</button>
          </div>
        </>
      )}
      {camp && vendorId && !order && !noPo && orders && (
        <table style={{ marginTop: 16 }}>
          <thead><tr><th>PO #</th><th>Placed</th><th>Status</th><th className="num">Total</th><th className="num">Invoices keyed</th><th></th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td className="mono">{o.po_number ?? o.increment_id ?? `#${o.id}`}{o.increment_id && o.increment_id !== o.po_number && <span className="muted"> · order {o.increment_id}</span>}</td>
                <td className="mono">{o.created_at?.slice(0, 10) ?? '—'}</td>
                <td><span className="pill type">{label(o.status)}</span></td>
                <td className="num">{money(o.grand_total)}</td>
                <td className="num">{o.invoices ? o.invoices : '—'}</td>
                <td><button className="btn-sm" onClick={() => pickOrder(o.id)}>Select</button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="loading">No PO of {camp.name} from {vendor?.name ?? 'this vendor'} matches{poQ.trim() ? ` “${poQ.trim()}”` : ''}.</td></tr>}
          </tbody>
        </table>
      )}
      {camp && vendorId && !order && !noPo && !orders && <p className="loading">Loading POs…</p>}
      {order && (
        <>
          <p className="muted" style={{ margin: '0.9rem 0 0.6rem' }}>
            Against PO <span className="mono">{order.order.po_number ?? order.order.increment_id}</span> · {order.order.camp ?? camp?.name} · {order.order.vendor ?? vendor?.name} · {label(order.order.status)}{' '}
            <button className="btn-sm ghost" onClick={() => { setOrder(null); setLines([]); setErr(''); }}>Change PO</button>
          </p>
          <div className="form-row">
            <label className="field"><span>Invoice #</span><input className="search short" value={head.invoiceNo} onChange={(e) => setHead({ ...head, invoiceNo: e.target.value })} autoFocus /></label>
            <label className="field"><span>Invoice date</span><input className="search short" type="date" value={head.invoiceDate} onChange={(e) => setHead({ ...head, invoiceDate: e.target.value })} style={{ width: 150 }} /></label>
            <label className="field"><span>GL category</span><input className="search short" value={head.glCategory} onChange={(e) => setHead({ ...head, glCategory: e.target.value })} placeholder="—" /></label>
            <label className="field"><span>Tax</span><input className="search short" inputMode="decimal" value={head.tax} onChange={(e) => setHead({ ...head, tax: e.target.value })} placeholder="0.00" /></label>
          </div>
          <table style={{ marginTop: 16 }}>
            <thead><tr><th>Item #</th><th>Description</th><th className="num">Qty invoiced</th><th className="num">Price</th><th className="num">Ext.</th><th></th></tr></thead>
            <tbody>
              {lines.map((l, n) => (
                <tr key={n}>
                  <td className="mono">{l.itemNumber || '—'}</td>
                  <td>{l.itemName || '—'}</td>
                  <td className="num"><input className="iw-in" inputMode="decimal" value={l.qty} onChange={(e) => setLine(n, { qty: e.target.value })} /></td>
                  <td className="num"><input className="iw-in" inputMode="decimal" value={l.price} onChange={(e) => setLine(n, { price: e.target.value })} /></td>
                  <td className="num">{money((Number(l.qty) || 0) * (Number(l.price) || 0))}</td>
                  <td><button className="link" title="The invoice does not bill this line" onClick={() => setLines(lines.filter((_, i) => i !== n))}>Remove</button></td>
                </tr>
              ))}
              {lines.length === 0 && <tr><td colSpan={6} className="loading">No lines — change the PO, or it has nothing to invoice.</td></tr>}
            </tbody>
          </table>
          <div className="edi-actions">
            <span className="muted">Invoice total <strong>{money(Math.round(total * 100) / 100)}</strong>{head.tax.trim() ? ` + tax ${money(Number(head.tax) || 0)}` : ''}. Saving moves an acknowledged or pending PO to Invoice Received.</span>
            <button className="btn-sm" disabled={busy || !head.invoiceNo.trim() || !head.invoiceDate || lines.length === 0} onClick={save}>{busy ? 'Saving…' : 'Save invoice'}</button>
          </div>
        </>
      )}
      {err && <p className="msg bad" style={{ marginTop: 12 }}>⚠ {err}</p>}
    </section>
  );
}

type NewLineDraft = { reasonId: string; itemNumber: string; itemName: string; qtyInvoiced: string; qtyReceived: string; invoicedPrice: string; correctedPrice: string };
const EMPTY_LINE: NewLineDraft = { reasonId: '', itemNumber: '', itemName: '', qtyInvoiced: '1', qtyReceived: '', invoicedPrice: '', correctedPrice: '' };

// Ops action pane for one invoice — advance/override status, correct lines (each with its correction
// type; a "00 -" type is shown on the invoice but never in its adjustment), and add a line the PO does
// not carry (go/no-go #16).
function InvoiceDetailPane({ id, onBack }: { id: number; onBack: () => void }) {
  const [wf, setWf] = useState<InvoiceWorkflow | null>(null);
  const [reasons, setReasons] = useState<CorrectionReason[]>([]);
  const [corr, setCorr] = useState<Record<number, { qty: string; price: string; reasonId: string }>>({});
  const [newLine, setNewLine] = useState<NewLineDraft>(EMPTY_LINE);
  const [note, setNote] = useState('');
  const [overrideTo, setOverrideTo] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = () => api.commerceInvoiceWorkflow(id).then((w) => {
    setWf(w);
    setCorr(Object.fromEntries(w.lines.map((l) => [l.id, {
      qty: String(l.qty_received ?? l.qty_invoiced ?? ''),
      price: String(l.corrected_price ?? l.invoiced_price ?? ''),
      reasonId: l.correction_reason_id == null ? '' : String(l.correction_reason_id),
    }])));
  }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.commerceCorrectionReasons().then(setReasons).catch(() => setReasons([])); }, []);
  const reasonTitle = (rid: number | null) => reasons.find((r) => r.id === rid)?.title ?? null;
  const informational = (rid: number | null) => reasons.find((r) => r.id === rid)?.informational === true;

  const done = (m: string) => { setMsg(m); setErr(''); load(); };
  const fail = (e: unknown) => { setErr((e as Error).message); setMsg(''); };

  async function advance(to: string, override = false) {
    if (override && !window.confirm(`OVERRIDE the workflow and force ${label(wf?.status ?? '')} → ${label(to)}? This is audited.`)) return;
    setBusy('status');
    try { const r = await api.commerceInvoiceStatus(id, to, override); done(`Status → ${label(r.status)}${r.via === 'ops-override' ? ' (override, audited)' : ''}`); }
    catch (e) { fail(e); } finally { setBusy(''); }
  }

  async function saveCorrections() {
    if (!wf) return;
    const changes = wf.lines.flatMap((l) => {
      const c = corr[l.id]; if (!c) return [];
      const curQty = l.qty_received ?? l.qty_invoiced; const curPrice = l.corrected_price ?? l.invoiced_price;
      const nq = Number(c.qty); const np = Number(c.price);
      const out: LineCorrection = { lineId: l.id };
      if (Number.isFinite(nq) && nq !== Number(curQty)) out.correctedQty = nq;
      if (Number.isFinite(np) && np !== Number(curPrice)) out.correctedPrice = np;
      if (c.reasonId !== '' && Number(c.reasonId) !== l.correction_reason_id) out.correctionReasonId = Number(c.reasonId);
      if (out.correctedQty == null && out.correctedPrice == null && out.correctionReasonId == null) return [];
      if (note.trim()) out.note = note.trim();
      return [out];
    });
    if (changes.length === 0) { setErr('No line changes to save.'); return; }
    setBusy('corr');
    try { const r = await api.commerceCorrectLines(id, changes); done(`Corrected ${r.corrected} line(s) — adjustment ${money(r.adjustmentAmount)}, corrected total ${money(r.correctedTotal)}`); setNote(''); }
    catch (e) { fail(e); } finally { setBusy(''); }
  }

  async function addLine() {
    const n = (v: string) => (v.trim() === '' ? undefined : Number(v));
    const qi = n(newLine.qtyInvoiced); const ip = n(newLine.invoicedPrice);
    if (!newLine.reasonId) { setErr('Pick the line\'s correction type — every added line needs one.'); return; }
    if (qi == null || !(qi >= 0) || ip == null || !(ip >= 0)) { setErr('Enter the quantity and price the invoice bills for the line.'); return; }
    if (!newLine.itemNumber.trim() && !newLine.itemName.trim()) { setErr('Name the line — an item number or a description such as "Freight".'); return; }
    setBusy('add');
    try {
      const r = await api.commerceAddInvoiceLines(id, [{
        correctionReasonId: Number(newLine.reasonId), itemNumber: newLine.itemNumber.trim() || undefined, itemName: newLine.itemName.trim() || undefined,
        qtyInvoiced: qi, qtyReceived: n(newLine.qtyReceived), invoicedPrice: ip, correctedPrice: n(newLine.correctedPrice),
      }]);
      done(`Line added — adjustment ${money(r.adjustmentAmount)}, corrected total ${money(r.correctedTotal)}`);
      setNewLine(EMPTY_LINE);
    } catch (e) { fail(e); } finally { setBusy(''); }
  }

  async function uploadPdf() {
    if (!pdfFile) return;
    setBusy('pdf');
    try {
      const r = await api.commerceUploadInvoicePdf(id, pdfFile);
      done(r.advanced ? 'PDF attached — advanced to Has Pdf' : 'PDF attached');
      setPdfFile(null);
    } catch (e) {
      if ((e as { status?: number }).status === 503) setErr('Storage is not configured in this environment — PDF was not stored.');
      else fail(e);
    } finally { setBusy(''); }
  }

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to queue</button></div>
      {err && !wf && <p className="msg bad">⚠ {err}</p>}
      {wf && (
        <section className="card">
          <div className="iw-row">
            <span className="iw-lbl">Advance</span>
            {wf.allowedNext.map((s) => <button key={s} className="btn-sm" disabled={busy !== ''} onClick={() => advance(s)}>{label(s)}</button>)}
            {wf.allowedNext.length === 0 && <span className="muted">— terminal ({label(wf.status ?? '')}) —</span>}
            <span style={{ flex: 1 }} />
            <select className="sel" value={overrideTo} onChange={(e) => setOverrideTo(e.target.value)}>
              <option value="">Override to…</option>
              {STATUSES.filter((s) => s !== wf.status).map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
            <button className="btn-sm ghost" disabled={!overrideTo || busy !== ''} onClick={() => { advance(overrideTo, true); setOverrideTo(''); }}>Force</button>
          </div>
          <div className="iw-row">
            <span className="iw-lbl">PDF</span>
            <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)} />
            <button className="btn-sm ghost" disabled={!pdfFile || busy !== ''} onClick={uploadPdf}>{busy === 'pdf' ? 'Uploading…' : 'Upload'}</button>
            {wf.hasPdf && <a className="btn-sm ghost" href={api.commerceInvoicePdfUrl(id)} target="_blank" rel="noreferrer">View PDF</a>}
          </div>
          {(msg || err) && <p className={`msg ${err ? 'bad' : 'good'}`}>{err ? `⚠ ${err}` : msg}</p>}
          <div className="iw-totals">
            <span>Invoice <strong className="mono">{wf.invoiceNo ?? `#${wf.id}`}</strong> · {wf.po ? <>PO <span className="mono">{wf.po}</span></> : <span className="pill">no PO</span>}</span>
            <span>Invoice total <strong>{money(wf.invoiceTotal)}</strong></span>
            <span>Adjustment <strong className={wf.adjustmentAmount ? 'adj' : ''}>{money(wf.adjustmentAmount)}</strong></span>
            <span>Corrected total <strong>{money(wf.correctedTotal)}</strong></span>
          </div>
          {wf.locked && <p className="msg bad">Packaged — its lines cannot change. {wf.correction ? `To correct it, ${wf.correction}.` : ''}</p>}
          <div className="iw-lbl" style={{ margin: '0.5rem 0' }}>Line corrections</div>
          <table>
            <thead><tr><th>Item #</th><th>Description</th><th>Correction type</th><th className="num">Qty inv.</th><th className="num">Corrected qty</th><th className="num">Invoiced</th><th className="num">Corrected price</th><th className="num">Adjustment</th></tr></thead>
            <tbody>
              {wf.lines.map((l) => (
                <tr key={l.id}>
                  <td className="mono">{l.item_number ?? '—'}</td>
                  <td>{l.item_name ?? '—'}</td>
                  <td>
                    <select className="iw-sel" aria-label={`Correction type for line ${l.id}`} disabled={wf.locked} value={corr[l.id]?.reasonId ?? ''}
                      onChange={(e) => setCorr({ ...corr, [l.id]: { ...corr[l.id], reasonId: e.target.value } })}>
                      {/* '' is "leave as it is" — a line with no type yet reads as a dash. */}
                      {l.correction_reason_id == null && <option value="">—</option>}
                      {reasons.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                    </select>
                  </td>
                  <td className="num">{l.qty_invoiced ?? '—'}</td>
                  <td className="num"><input className="iw-in" inputMode="decimal" disabled={wf.locked} value={corr[l.id]?.qty ?? ''} onChange={(e) => setCorr({ ...corr, [l.id]: { ...corr[l.id], qty: e.target.value } })} /></td>
                  <td className="num">{money(l.invoiced_price)}</td>
                  <td className="num"><input className="iw-in" inputMode="decimal" disabled={wf.locked} value={corr[l.id]?.price ?? ''} onChange={(e) => setCorr({ ...corr, [l.id]: { ...corr[l.id], price: e.target.value } })} /></td>
                  <td className={'num' + (l.adjusted_extended && !informational(l.correction_reason_id) ? ' adj' : '')}>
                    {l.adjusted_extended ? money(l.adjusted_extended) : '—'}
                    {informational(l.correction_reason_id) && l.adjusted_extended ? <span className="pill info" style={{ marginLeft: 4 }} title={`${reasonTitle(l.correction_reason_id)} — shown, not owed`}>not owed</span> : null}
                  </td>
                </tr>
              ))}
              {wf.lines.length === 0 && <tr><td colSpan={8} className="loading">No line items{wf.po ? '' : ' — an invoice with no PO starts with none'}.</td></tr>}
            </tbody>
          </table>
          <div className="edi-actions">
            <input className="search" placeholder="Correction note (optional)…" value={note} onChange={(e) => setNote(e.target.value)} style={{ maxWidth: 340 }} />
            <button className="btn-sm" disabled={busy !== '' || wf.locked} onClick={saveCorrections}>{busy === 'corr' ? 'Saving…' : 'Save corrections'}</button>
          </div>
          {!wf.locked && (
            <>
              <div className="iw-lbl" style={{ margin: '1rem 0 0.5rem' }}>Add a line the PO does not carry</div>
              <div className="iw-row">
                <select className="iw-sel" aria-label="New line correction type" value={newLine.reasonId} onChange={(e) => setNewLine({ ...newLine, reasonId: e.target.value })}>
                  <option value="">Correction type…</option>
                  {reasons.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                </select>
                <input className="iw-text" aria-label="New line item number" placeholder="Item #" value={newLine.itemNumber} onChange={(e) => setNewLine({ ...newLine, itemNumber: e.target.value })} />
                <input className="iw-text" aria-label="New line description" placeholder="Description (e.g. Freight)" value={newLine.itemName} onChange={(e) => setNewLine({ ...newLine, itemName: e.target.value })} />
                <input className="iw-in" aria-label="Qty invoiced" placeholder="Qty inv." inputMode="decimal" value={newLine.qtyInvoiced} onChange={(e) => setNewLine({ ...newLine, qtyInvoiced: e.target.value })} />
                <input className="iw-in" aria-label="Qty received" placeholder="Qty rec." inputMode="decimal" value={newLine.qtyReceived} onChange={(e) => setNewLine({ ...newLine, qtyReceived: e.target.value })} />
                <input className="iw-in" aria-label="Invoiced price" placeholder="Invoiced $" inputMode="decimal" value={newLine.invoicedPrice} onChange={(e) => setNewLine({ ...newLine, invoicedPrice: e.target.value })} />
                <input className="iw-in" aria-label="Corrected price" placeholder="Corrected $" inputMode="decimal" value={newLine.correctedPrice} onChange={(e) => setNewLine({ ...newLine, correctedPrice: e.target.value })} />
                <button className="btn-sm" disabled={busy !== ''} onClick={addLine}>{busy === 'add' ? 'Adding…' : 'Add line'}</button>
              </div>
              <p className="muted" style={{ fontSize: '0.82rem', margin: 0 }}>Received and corrected default to what was invoiced — a line on the bill and owed. A charge the camp does not owe: received 0 (or corrected $0). A "00 -" type is shown on the invoice but never adjusts it.</p>
            </>
          )}
        </section>
      )}
      {!wf && !err && <p className="loading">Loading…</p>}
    </div>
  );
}

// The grid's mass actions on the selected invoices (Adobe's correction_invoice_listing massaction):
// export their corrections for the client, for the vendor or to Excel, and set one GL code on all of
// them. The GL choices are the catalog's categories and the selected camps' own codes; a packaged
// invoice is locked, and commerce refuses the whole change, naming it.
function BulkBar({ sel, onClear, onChanged }: { sel: Set<number>; onClear: () => void; onChanged: () => void }) {
  const ids = useMemo(() => [...sel].sort((a, b) => a - b), [sel]);
  const [opts, setOpts] = useState<GlOptions | null>(null);
  const [gl, setGl] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    setOpts(null); setGl(''); setErr('');
    if (ids.length === 0) return;
    let live = true;
    const t = setTimeout(() => {
      api.commerceGlOptions(ids).then((o) => live && setOpts(o)).catch((e) => live && setErr(errText(e)));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [ids]);

  async function exportAs(kind: CorrectionsKind) {
    setBusy(kind); setErr(''); setMsg('');
    try { await api.downloadCorrections(kind, ids); } catch (e) { setErr(errText(e)); } finally { setBusy(''); }
  }

  async function applyGl() {
    if (!gl) return;
    if (!window.confirm(`Set GL code "${gl}" on ${ids.length} invoice${ids.length === 1 ? '' : 's'} and their orders? Each change is logged.`)) return;
    setBusy('gl'); setErr(''); setMsg('');
    try {
      const r = await api.commerceBulkGlCode(ids, gl);
      setMsg(`GL code “${r.glCode}” set on ${r.updated} invoice${r.updated === 1 ? '' : 's'}${r.unchanged ? ` (${r.unchanged} already had it)` : ''}`
        + `${r.orders ? ` and ${r.orders} order${r.orders === 1 ? '' : 's'}` : ''}. Make the same change in Adobe.`);
      onChanged();
    } catch (e) { setErr(errText(e)); } finally { setBusy(''); }
  }

  return (
    <section className="card bulk-bar">
      <div className="iw-pkgbar">
        <strong>{ids.length} selected</strong>
        <button className="btn-sm ghost" onClick={onClear}>Clear</button>
        <span className="bulk-sep" />
        <span className="iw-lbl">Export corrections</span>
        <button className="btn-sm ghost" disabled={busy !== ''} onClick={() => exportAs('client')}>{busy === 'client' ? 'Exporting…' : 'For client'}</button>
        <button className="btn-sm ghost" disabled={busy !== ''} onClick={() => exportAs('vendor')}>{busy === 'vendor' ? 'Exporting…' : 'For vendor'}</button>
        <button className="btn-sm ghost" disabled={busy !== ''} onClick={() => exportAs('excel')}>{busy === 'excel' ? 'Exporting…' : 'To Excel'}</button>
        <span className="bulk-sep" />
        <span className="iw-lbl">GL code</span>
        <select className="sel" aria-label="GL code to set" value={gl} disabled={!opts} onChange={(e) => setGl(e.target.value)}>
          <option value="">{opts ? 'Choose a GL code…' : 'Loading…'}</option>
          {opts && opts.categories.length > 0 && (
            <optgroup label="Categories">{opts.categories.map((c) => <option key={`c:${c}`} value={c}>{c}</option>)}</optgroup>
          )}
          {opts && opts.campCodes.length > 0 && (
            <optgroup label="Camp GL codes">
              {opts.campCodes.map((c) => <option key={`g:${c.code}`} value={c.code}>{c.code} — {c.camps.length > 2 ? `${c.camps.length} camps` : c.camps.join(', ')}</option>)}
            </optgroup>
          )}
        </select>
        <button className="btn-sm ghost" disabled={!gl || busy !== ''} onClick={applyGl}>{busy === 'gl' ? 'Applying…' : 'Apply'}</button>
      </div>
      {(msg || err) && <p className={`msg ${err ? 'bad' : 'good'}`}>{err ? `⚠ ${err}` : msg}</p>}
    </section>
  );
}

export function InvoicesPanel() {
  const [tab, setTab] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: InvoiceQueueRow[]; total: number } | null>(null);
  const [pkgs, setPkgs] = useState<PackageRow[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [keying, setKeying] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [pkgName, setPkgName] = useState('');
  const [pkgBusy, setPkgBusy] = useState(false);
  const [pkgMsg, setPkgMsg] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setErr('');
    if (tab === 'history') return;
    if (tab === 'pkgs') { api.commercePackages(q).then(setPkgs).catch((e) => setErr((e as Error).message)); return; }
    api.commerceInvoiceQueue(tab, q, page).then((d) => setData({ rows: d.rows, total: d.total })).catch((e) => setErr((e as Error).message));
  };
  useEffect(() => {
    let live = true;
    setData(null); setPkgs(null);
    const t = setTimeout(() => { if (live) load(); }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [tab, q, page]);

  async function createPackage() {
    if (sel.size === 0) return;
    setPkgBusy(true); setPkgMsg('');
    try {
      const r = await api.commerceCreatePackage([...sel], pkgName.trim() || undefined);
      setPkgMsg(`Package ${r.name} (#${r.id}) created with ${r.count} invoice(s).`);
      setSel(new Set()); setPkgName('');
      load();
    } catch (e) { setPkgMsg(`⚠ ${(e as Error).message}`); } finally { setPkgBusy(false); }
  }

  if (open != null) return <InvoiceDetailPane id={open} onBack={() => { setOpen(null); load(); }} />;

  const per = 50;
  const pages = data ? Math.max(1, Math.ceil(data.total / per)) : 1;
  const grid = tab !== 'pkgs' && tab !== 'history';
  const pageIds = (data?.rows ?? []).map((i) => i.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => sel.has(id));
  const toggle = (id: number, on: boolean) => { const n = new Set(sel); if (on) n.add(id); else n.delete(id); setSel(n); };
  const togglePage = (on: boolean) => { const n = new Set(sel); for (const id of pageIds) { if (on) n.add(id); else n.delete(id); } setSel(n); };
  const pickTab = (t: string) => { setTab(t); setPage(1); setSel(new Set()); setPkgMsg(''); };

  return (
    <div className="stack">
      <h1>Invoices</h1>
      <p className="sub">AP workflow — key paper invoices against their POs, work the queue, correct lines, advance status, and package approved invoices. Select invoices to export their corrections or change their GL code.</p>
      {keying && <KeyInvoiceForm onClose={() => setKeying(false)} onSaved={(id) => { setKeying(false); setOpen(id); }} />}
      <div className="toolbar">
        <button className={'chip' + (tab === '' ? ' on' : '')} onClick={() => pickTab('')}>All</button>
        {STATUSES.map((s) => <button key={s} className={'chip' + (tab === s ? ' on' : '')} onClick={() => pickTab(s)}>{label(s)}</button>)}
        <button className={'chip' + (tab === 'pkgs' ? ' on' : '')} onClick={() => pickTab('pkgs')}>Packages</button>
        <button className={'chip' + (tab === 'history' ? ' on' : '')} onClick={() => pickTab('history')}>Status history</button>
      </div>
      {tab === 'history' ? <InvoiceHistoryLog onOpen={setOpen} /> : (
      <>
      <div className="toolbar">
        <input className="search" placeholder={tab === 'pkgs' ? 'Search packages…' : 'Search invoice #, PO or vendor…'} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <span style={{ flex: 1 }} />
        {!keying && <button className="btn-sm" onClick={() => setKeying(true)}>+ Key invoice</button>}
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      {grid && sel.size > 0 && <BulkBar sel={sel} onClear={() => setSel(new Set())} onChanged={load} />}
      {grid && sel.size > 0 && <p className="note">Adobe is still the live system until the cutover. A GL code changed here lasts until the next data load unless it is also changed in Adobe.</p>}
      {tab === 'approved' && (
        <section className="card iw-pkgbar">
          <span>{sel.size} selected</span>
          <input className="search" placeholder="Package name (blank = auto YYYY_n)…" value={pkgName} onChange={(e) => setPkgName(e.target.value)} style={{ maxWidth: 280 }} />
          <button className="btn-sm" disabled={sel.size === 0 || pkgBusy} onClick={createPackage}>{pkgBusy ? 'Packaging…' : 'Create package'}</button>
          {pkgMsg && <span className={pkgMsg.startsWith('⚠') ? 'msg bad' : 'muted'}>{pkgMsg}</span>}
        </section>
      )}
      {tab === 'pkgs' ? (
        <section className="card">
          <table>
            <thead><tr><th>Package</th><th>Camp</th><th className="num">Invoices</th><th className="num">Total</th><th>Created</th></tr></thead>
            <tbody>
              {(pkgs ?? []).map((p) => (
                <tr key={p.id}><td className="mono">{p.name ?? `#${p.id}`}</td><td>{p.camp ?? '—'}</td><td className="num">{p.invoices}</td><td className="num">{money(p.total)}</td><td className="muted">{p.created_at?.slice(0, 10) ?? '—'}</td></tr>
              ))}
              {pkgs && pkgs.length === 0 && <tr><td colSpan={5} className="loading">No packages.</td></tr>}
            </tbody>
          </table>
          {!pkgs && !err && <p className="loading">Loading…</p>}
        </section>
      ) : (
        <section className="card">
          <table>
            <thead><tr>
              <th><input type="checkbox" aria-label="Select every invoice on this page" checked={allOnPage} onChange={(e) => togglePage(e.target.checked)} /></th>
              <th>Invoice #</th><th>Vendor</th><th>Camp</th><th>PO</th><th>Status</th><th>GL</th><th className="num">Invoiced</th><th className="num">Adjustment</th><th>PDF</th>
            </tr></thead>
            <tbody>
              {(data?.rows ?? []).map((i) => (
                <tr key={i.id} className="row-click" onClick={() => setOpen(i.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select invoice ${i.invoice_no ?? i.id}`} checked={sel.has(i.id)} onChange={(e) => toggle(i.id, e.target.checked)} />
                  </td>
                  <td className="mono">{i.invoice_no ?? `#${i.id}`}{i.source === 'keyed' && <span className="pill" style={{ marginLeft: 4 }}>new</span>}</td>
                  <td>{i.vendor ?? '—'}</td>
                  <td className="muted">{i.camp ?? '—'}</td>
                  <td className="mono muted">{i.po ?? '—'}</td>
                  <td><span className="pill type">{label(i.workflow_status)}</span></td>
                  <td className="muted">{i.gl_category ?? '—'}</td>
                  <td className="num">{money(i.invoice_total)}</td>
                  <td className={'num' + (i.adjustment_amount ? ' adj' : '')}>{i.adjustment_amount ? money(i.adjustment_amount) : '—'}</td>
                  <td className="muted">{i.has_pdf_file ? '✓' : '—'}</td>
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={11} className="loading">No invoices{tab ? ` in ${label(tab)}` : ''}.</td></tr>}
            </tbody>
          </table>
          {!data && !err && <p className="loading">Loading…</p>}
          {data && pages > 1 && (
            <div className="iw-pager">
              <button className="btn-sm ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
              <span className="muted">page {page} / {pages} · {data.total.toLocaleString()} invoices</span>
              <button className="btn-sm ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next →</button>
            </div>
          )}
        </section>
      )}
      </>
      )}
    </div>
  );
}
